import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Bot,
  Link2,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Zap,
  PlusCircle,
  AlertOctagon,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/create-website", icon: PlusCircle, label: "Create Website" },
  { to: "/businesses", icon: Briefcase, label: "Businesses" },
  { to: "/studio", icon: Bot, label: "AI Agents" },
  { to: "/affiliates", icon: Link2, label: "Affiliates" },
  { to: "/dlq", icon: AlertOctagon, label: "Dead Letter Queue" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { tenant, logout } = useAuth();

  // Top-level POST to the API logout, which continues to the Titanium portal
  // logout and lands back on /login (no client-side navigation needed).
  function handleLogout() {
    logout();
  }

  return (
    <aside
      className={`
        relative flex flex-col h-full bg-gray-900 border-r border-gray-800
        transition-all duration-200 ease-in-out
        ${collapsed ? "w-16" : "w-56"}
      `}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-800 min-h-[64px]">
        <div className="flex-shrink-0 w-8 h-8 bg-brand-600 rounded-lg flex items-center justify-center">
          <Zap className="w-4 h-4 text-white" />
        </div>
        {!collapsed && (
          <span className="text-sm font-semibold text-white truncate">
            AffiliateStream
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-y-auto scrollbar-thin">
        <ul className="space-y-0.5 px-2">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-brand-600/20 text-brand-400 font-medium"
                      : "text-gray-400 hover:text-gray-100 hover:bg-gray-800"
                  }`
                }
                title={collapsed ? label : undefined}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {!collapsed && <span>{label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* User + logout */}
      <div className="border-t border-gray-800 p-2">
        {!collapsed && tenant && (
          <div className="px-2 py-2 mb-1">
            <p className="text-xs font-medium text-gray-200 truncate">{tenant.name}</p>
            <p className="text-xs text-gray-500 truncate">{tenant.email}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg text-sm text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors"
          title={collapsed ? "Logout" : undefined}
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 z-10 w-6 h-6 bg-gray-700 border border-gray-600 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? (
          <ChevronRight className="w-3 h-3 text-gray-300" />
        ) : (
          <ChevronLeft className="w-3 h-3 text-gray-300" />
        )}
      </button>
    </aside>
  );
}
